import React, { useState } from "react";

import { fromPairs, merge } from "lodash/fp";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Divider,
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Button,
} from "@mui/material";

import ReadAssistantPreviewModal from "@app/analytics/account/components/ReadAssistantPreviewModal";
import SettingMultiSelect from "@app/analytics/account/components/SettingMultiSelect";
import SettingsTitle from "@app/analytics/account/components/SettingsTitle";
import ZoomOnlyChip from "@app/analytics/account/components/ZoomOnlyChip";
import {
  BOT_CONFIG_MULTI_SELECT_PROPS,
  getBotConfigRadioValue,
  getEnabledBotScreens,
  getUpdatedSettingsFromRadioValue,
} from "@app/analytics/account/utils";
import WorkspaceSettingsAccordion from "@app/analytics/account/WorkspaceSettings/components/WorkspaceSettingsAccordion";
import Radio from "@app/analytics/components/Radio";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { TOOLTIPS } from "@app/common/content";
import { BotConfig } from "@app/common/types";
import { UploadLogoBody } from "@app/common/workspaces/WorkspaceContext";

import { DefaultBadge } from "@rui/Badge";

import CustomizeLogoSection from "./CustomizeLogoSection";
import ReadAssistantNameSection from "./ReadAssistantNameSection";
import {
  AssistantDisplayOptionValue,
  SettingsModuleProps,
} from "../../../types";
import WorkspaceCanEditDropdown from "../../components/WorkspaceCanEditDropdown";
import {
  ASSISTANT_DISPLAY_OPTIONS,
  ASSISTANT_DISPLAY_CONFIG,
} from "../../constants";

interface ReadAssistantModuleProps extends SettingsModuleProps {
  onUploadCustomBotLogo: (opts: UploadLogoBody) => void;
  onDeleteCustomBotLogo: () => void;
}

const ReadAssistantModule = ({
  onSettingUpdate,
  onUploadCustomBotLogo,
  onDeleteCustomBotLogo,
  settings,
  workspaceIsInactive,
}: ReadAssistantModuleProps) => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const handleCanUpdateBotConfigChange = (newValue: boolean) => {
    onSettingUpdate({ botConfigIsUserEditable: newValue });
  };

  const updateBotConfigSettings = (newSettings: Partial<BotConfig>) => {
    const mergedSettings = merge({ ...settings.botConfig }, newSettings);
    onSettingUpdate({ botConfig: mergedSettings });
  };

  const handleBotConfigSettingsRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.currentTarget.value as AssistantDisplayOptionValue;
    return updateBotConfigSettings(
      getUpdatedSettingsFromRadioValue(value, true)
    );
  };

  const handleBotConfigSettingsCheckboxChange = (newValues: string[]) => {
    const newSettings = fromPairs(
      BOT_CONFIG_MULTI_SELECT_PROPS.items.map((item) => [
        item.value,
        newValues.includes(item.value),
      ])
    );
    updateBotConfigSettings(newSettings);
  };

  const enabledBotScreens = getEnabledBotScreens(settings.botConfig);

  const displayRadioGroupValue = getBotConfigRadioValue(
    settings.botConfig,
    true
  );

  return (
    <>
      <ReadAssistantPreviewModal
        onClose={() => setModalOpen(false)}
        open={modalOpen}
      />
      <WorkspaceSettingsAccordion
        title="Assistant"
        subtitle="Set Read Assistant preferences for this Workspace"
      >
        <Stack spacing={1.5}>
          <SettingsTitle
            title={ASSISTANT_DISPLAY_CONFIG.title}
            endIcon={<ZoomOnlyChip />}
          />
          <WorkspaceCanEditDropdown
            disabled={workspaceIsInactive}
            onUpdate={handleCanUpdateBotConfigChange}
            selectedValue={settings.botConfigIsUserEditable}
            name={ASSISTANT_DISPLAY_CONFIG.name}
            tooltip={
              workspaceIsInactive ? TOOLTIPS.upsell.upgradeToPro : undefined
            }
          />
        </Stack>
        <UpsellTooltipWrapper show={workspaceIsInactive} placement="right">
          <RadioGroup
            value={displayRadioGroupValue}
            onChange={handleBotConfigSettingsRadioChange}
            sx={{ mb: workspaceIsInactive ? 0 : 2 }}
          >
            {ASSISTANT_DISPLAY_OPTIONS.map((option) => (
              <Stack key={option.label} direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Radio
                      value={option.value}
                      disabled={workspaceIsInactive}
                    />
                  }
                  label={
                    <Typography
                      variant="bodySecondaryReg"
                      sx={{ color: "shades.neutral.600" }}
                    >
                      {option.label}
                    </Typography>
                  }
                />
                {option.default && <DefaultBadge />}
              </Stack>
            ))}
            {displayRadioGroupValue === "display_on_metrics_on" && (
              <SettingMultiSelect
                disabled={workspaceIsInactive}
                {...BOT_CONFIG_MULTI_SELECT_PROPS}
                value={enabledBotScreens}
                onChange={handleBotConfigSettingsCheckboxChange}
                sx={{ ml: 5 }}
              />
            )}
          </RadioGroup>
        </UpsellTooltipWrapper>
        <Button
          variant="text"
          sx={{
            width: "fit-content",
            color: "shades.neutral.800",
          }}
          endIcon={<OpenInNewIcon sx={{ width: "12px", height: "12px" }} />}
        >
          <Typography
            sx={{
              textDecoration: "underline",
              "&:hover": { textDecoration: "underline" },
            }}
            variant="labelLink"
            onClick={() => setModalOpen(true)}
          >
            Compare these settings
          </Typography>
        </Button>
        <Divider />
        <CustomizeLogoSection
          onUploadCustomBotLogo={onUploadCustomBotLogo}
          onDeleteCustomBotLogo={onDeleteCustomBotLogo}
          settings={settings}
          onSettingUpdate={onSettingUpdate}
          workspaceIsInactive={workspaceIsInactive}
        />
        <Divider />
        <ReadAssistantNameSection
          onSettingUpdate={onSettingUpdate}
          settings={settings}
          workspaceIsInactive={workspaceIsInactive}
        />
      </WorkspaceSettingsAccordion>
    </>
  );
};

export default ReadAssistantModule;
